import { useEffect, useState } from 'react'
import { Button } from '@heroui/react'
import { api } from '../../lib/api'
import {
  attributeFormValues,
  currentManifest,
  isValidInteger,
  placeableApps,
  placedAppName,
  typedAttributes,
} from '../../lib/screenApps'
import { explain } from '../../lib/staffErrors'
import type { ScreenApp, Unit } from '../../lib/types'
import { useStaffText } from '../../ui/StaffText'
import { Check, Messages, Num, Text } from '../parts'
import { Pick } from '../setupKit'
import '../../styles/branch.css'

/**
 * Which screen app a kiosk or hall board runs, and the values chosen for it.
 *
 * A part of its own, with a Save of its own, rather than part of the rest of this
 * panel's: PUT /units/{id}/app is a different route from PATCH /units/{id},
 * because placing an app reaches the running machine at once and reconciles its
 * attributes against the app's current manifest — not the ordinary save this
 * panel otherwise does.
 *
 * Shown even when `moduleOn` is false, as long as the machine already has an app
 * placed: taking one off is never behind the module server-side (a licence
 * downgrade must not strand a machine with nobody able to clear it), so this still
 * offers that.
 */
export function ScreenAppSection({
  unit,
  kind,
  apps,
  moduleOn,
  onSaved,
}: {
  unit: Unit
  kind: 'kiosk' | 'display'
  apps: ScreenApp[]
  moduleOn: boolean
  onSaved: () => void
}) {
  const { lang, say } = useStaffText()
  const appOf = (id: string) => apps.find((a) => a.id === id)

  const [appId, setAppId] = useState(unit.appId ?? '')
  const [values, setValues] = useState<Record<string, string | boolean>>(() => {
    const chosen = appOf(unit.appId ?? '')
    return attributeFormValues(chosen ? currentManifest(chosen) : undefined, unit.appAttributes)
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  // Follows this machine's own saved placement: it resets when the answer to a
  // save comes back, and picks up the chosen app's manifest once the list of
  // apps (which arrives after the machine does) is here.
  useEffect(() => {
    setAppId(unit.appId ?? '')
    const chosen = appOf(unit.appId ?? '')
    setValues(attributeFormValues(chosen ? currentManifest(chosen) : undefined, unit.appAttributes))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit.id, unit.appId, unit.appAttributes, apps])

  const place = async (nextAppId: string, attrs: Record<string, string | number | boolean>, said: string) => {
    setBusy(true)
    setError('')
    setNotice('')
    try {
      await api.placeScreenApp(unit.id, { appId: nextAppId, attributes: attrs })
      setNotice(said)
      onSaved()
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  if (!moduleOn) {
    const { name, retired } = placedAppName(appOf(unit.appId ?? ''), unit.appId ?? '')
    return (
      <fieldset className="field branch-app">
        <legend title={say('screenApps.moduleOffHint')}>{say('screenApps.pick')}</legend>
        <p>{retired ? say('catalogue.units.retiredSuffix', { name }) : name}</p>
        <Messages error={error} notice={notice} />
        <Button
          variant="secondary"
          size="sm"
          className="admin-start"
          isDisabled={busy}
          onPress={() => void place('', {}, say('screenApps.placedDesign'))}
        >
          {busy ? say('common.saving') : say('screenApps.takeOff')}
        </Button>
      </fieldset>
    )
  }

  const chosenApp = appOf(appId)
  const manifest = chosenApp ? currentManifest(chosenApp) : undefined
  const savedApp = appOf(unit.appId ?? '')
  const savedValues = attributeFormValues(savedApp ? currentManifest(savedApp) : undefined, unit.appAttributes)
  const dirty = appId !== (unit.appId ?? '') || JSON.stringify(values) !== JSON.stringify(savedValues)

  const chooseApp = (next: string) => {
    setAppId(next)
    const chosen = appOf(next)
    const current = next === (unit.appId ?? '') ? unit.appAttributes : undefined
    setValues(attributeFormValues(chosen ? currentManifest(chosen) : undefined, current))
  }

  const kindApps = placeableApps(apps, kind)
  // A placement that is no longer a live choice — most often because its app has
  // since been retired — is still the shown value, added as a disabled option so
  // it can be seen, and left, but never chosen again.
  const orphan = appId && !kindApps.some((a) => a.id === appId) ? placedAppName(appOf(appId), appId) : null
  const badAttribute = manifest?.attributes.find(
    (attr) => attr.type === 'integer' && !isValidInteger(String(values[attr.key] ?? '')),
  )

  return (
    <fieldset className="field branch-app">
      <legend>{say('screenApps.pick')}</legend>
      <Pick
        label={say('screenApps.pick')}
        value={appId}
        onChange={chooseApp}
        options={[
          { value: '', label: say('screenApps.design') },
          ...kindApps.map((a) => ({ value: a.id, label: a.name, note: a.key })),
          ...(orphan ? [{ value: appId, label: say('catalogue.units.retiredSuffix', { name: orphan.name }), disabled: true }] : []),
        ]}
      />
      {manifest &&
        manifest.attributes.length > 0 &&
        manifest.attributes.map((attr) =>
          attr.type === 'boolean' ? (
            <Check
              key={attr.key}
              label={attr.name}
              checked={values[attr.key] === true}
              onChange={(v) => setValues((s) => ({ ...s, [attr.key]: v }))}
            />
          ) : (
            <label key={attr.key} className="field">
              <span>{attr.name}</span>
              {attr.type === 'integer' ? (
                <Num value={Number(values[attr.key]) || 0} onChange={(n) => setValues((s) => ({ ...s, [attr.key]: String(n) }))} />
              ) : (
                <Text value={String(values[attr.key] ?? '')} onChange={(v) => setValues((s) => ({ ...s, [attr.key]: v }))} />
              )}
            </label>
          ),
        )}
      {badAttribute && <p className="field-error branch-flat">{say('screenApps.badInteger', { name: badAttribute.name })}</p>}
      <Messages error={error} notice={notice} />
      <Button
        variant="secondary"
        size="sm"
        className="admin-start"
        isDisabled={!dirty || busy || Boolean(badAttribute)}
        onPress={() =>
          void place(
            appId,
            typedAttributes(manifest, values),
            chosenApp ? say('screenApps.placedApp', { name: chosenApp.name }) : say('screenApps.placedDesign'),
          )
        }
      >
        {/* Its own words, not "Save": the panel has a Save of its own, and two
            buttons with one name are one button to anybody reaching them by name. */}
        {busy ? say('common.saving') : say('screenApps.saveApp')}
      </Button>
    </fieldset>
  )
}
